import React, { useState, useEffect, useCallback } from 'react';
import { pullOutAPI } from '../api';
import { Search, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, X, Package } from 'lucide-react';

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val || 0);
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const emptyForm = {
  date: '', plate_number: '', document_no: '', customer: '',
  amount: '', document_date: '', driver: '', helper: '', helper2: '',
  date_return_warehouse: '', remarks: ''
};

export default function PullOuts() {
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await pullOutAPI.getAll({ page, limit: 15, search });
      setRecords(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error('Load error:', err);
    }
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  function handleChange(e) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      if (editId) {
        await pullOutAPI.update(editId, form);
      } else {
        await pullOutAPI.create(form);
      }
      setShowModal(false);
      setForm(emptyForm);
      setEditId(null);
      load(pagination.page);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  function openEdit(row) {
    setForm({
      date: row.date ? new Date(row.date).toISOString().slice(0, 10) : '',
      plate_number: row.plate_number || '',
      document_no: row.document_no || '',
      customer: row.customer || '',
      amount: row.amount || '',
      document_date: row.document_date ? new Date(row.document_date).toISOString().slice(0, 10) : '',
      driver: row.driver || '',
      helper: row.helper || '',
      helper2: row.helper2 || '',
      date_return_warehouse: row.date_return_warehouse ? new Date(row.date_return_warehouse).toISOString().slice(0, 10) : '',
      remarks: row.remarks || ''
    });
    setEditId(row.id);
    setShowModal(true);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this pull-out record?')) return;
    try {
      await pullOutAPI.delete(id);
      load(pagination.page);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  return (
    <div>
      <div className="table-container">
        <div className="table-header table-title-row">
          <h3>Pull-Out Records ({pagination.total.toLocaleString()} total)</h3>
        </div>

        <div className="table-filters-row">
          <div className="table-search-wrap">
            <Search size={16} className="table-search-icon" />
            <input
              className="search-input table-search-input"
              placeholder="Search customer, plate, driver..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button className="btn btn-primary" onClick={() => { setForm(emptyForm); setEditId(null); setShowModal(true); }}>
            <Plus size={16} /> Add Record
          </button>
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Plate Number</th>
                <th>Document #</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Driver</th>
                <th>Helpers</th>
                <th>Return Date</th>
                <th>Remarks</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40 }}>Loading...</td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40 }}>No records found</td></tr>
              ) : records.map(r => (
                <tr key={r.id}>
                  <td>{formatDate(r.date)}</td>
                  <td>{r.plate_number || '-'}</td>
                  <td>{r.document_no || '-'}</td>
                  <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.customer || '-'}</td>
                  <td className="amount">{formatCurrency(r.amount)}</td>
                  <td>{r.driver || '-'}</td>
                  <td>{[r.helper, r.helper2].filter(Boolean).join(', ') || '-'}</td>
                  <td>{formatDate(r.date_return_warehouse)}</td>
                  <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.remarks || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(r)} title="Edit"><Edit2 size={14} /></button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(r.id)} title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pagination">
        <div className="pagination-info">
          Page {pagination.page} of {pagination.totalPages}
        </div>
        <div className="pagination-buttons">
          <button className="page-btn" disabled={pagination.page <= 1} onClick={() => load(pagination.page - 1)}><ChevronLeft size={16} /></button>
          <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => load(pagination.page + 1)}><ChevronRight size={16} /></button>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editId ? 'Edit Pull-Out Record' : 'New Pull-Out Record'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Date</label>
                    <input type="date" name="date" value={form.date} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Plate Number</label>
                    <input name="plate_number" value={form.plate_number} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Document Number</label>
                    <input name="document_no" value={form.document_no} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Document Date</label>
                    <input type="date" name="document_date" value={form.document_date} onChange={handleChange} />
                  </div>
                  <div className="form-group full-width">
                    <label>Customer</label>
                    <input name="customer" value={form.customer} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Amount (PHP)</label>
                    <input type="number" step="0.01" name="amount" value={form.amount} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Driver</label>
                    <input name="driver" value={form.driver} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Helper 1</label>
                    <input name="helper" value={form.helper} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Helper 2</label>
                    <input name="helper2" value={form.helper2} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Date Returned to Warehouse</label>
                    <input type="date" name="date_return_warehouse" value={form.date_return_warehouse} onChange={handleChange} />
                  </div>
                  <div className="form-group full-width">
                    <label>Remarks</label>
                    <textarea name="remarks" value={form.remarks} onChange={handleChange} rows={2} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
